<template>
  <div class="agent-center">
    <!-- 页面头部 -->
    <div class="page-header">
      <button class="back-button" @click="$router.back()">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        返回
      </button>
      <h1 class="page-title">代理中心</h1>
    </div>

    <!-- 导航标签 -->
    <div class="nav-tabs">
      <button
        class="tab-button"
        :class="{ active: activeTab === 'info' }"
        @click="activeTab = 'info'"
      >
        我的信息
      </button>
      <button
        class="tab-button"
        :class="{ active: activeTab === 'promotion' }"
        @click="activeTab = 'promotion'"
      >
        推广数据
      </button>
      <button
        class="tab-button"
        :class="{ active: activeTab === 'withdrawal' }"
        @click="activeTab = 'withdrawal'"
      >
        提现数据
      </button>
    </div>

    <!-- 内容区域 -->
    <div class="content-area">
      <!-- 我的信息 -->
      <div v-if="activeTab === 'info'" class="info-section">
        <div class="loading" v-if="loadingStats">
          <p>加载中...</p>
        </div>
        <div v-else>
          <div class="stats-bar">
            <div class="stat-item">
              <div class="value">{{ agentStats.promotedUsers || 0 }}</div>
              <div class="label-bar">推广用户</div>
            </div>
            <div class="stat-item">
              <div class="value">¥{{ (agentStats.totalEarnings || 0).toFixed(2) }}</div>
              <div class="label-bar">累计收益</div>
            </div>
            <div class="stat-item">
              <div class="value">¥{{ (agentStats.availableWithdrawal || 0).toFixed(2) }}</div>
              <div class="label-bar">可提现</div>
            </div>
          </div>

          <div class="account-overview">
            <div class="item">
              <span class="label">注册时间</span>
              <span class="value">{{ formatDate(agent.createdAt) }}</span>
            </div>
            <div class="item">
              <span class="label">代理等级</span>
              <span class="value">{{ getAgentLevel(agent.link) }}</span>
            </div>
            <div class="item">
              <span class="label">返佣比例</span>
              <span class="value" style="color: #4CAF50;">{{ commissionRate }}%</span>
            </div>
          </div>

          <!-- 个人信息模块 -->
          <div class="personal-info-section">
            <h3>个人信息</h3>
            <form @submit.prevent="savePersonalInfo">
              <div class="form-group">
                <label for="wechatId">微信ID</label>
                <input
                  type="text"
                  id="wechatId"
                  v-model="personalInfo.wechatId"
                  placeholder="请输入您的微信ID"
                />
              </div>

              <div class="form-group">
                <label for="wechatPublic">公众号</label>
                <input
                  type="text"
                  id="wechatPublic"
                  v-model="personalInfo.wechatPublic"
                  placeholder="请输入您的公众号名称"
                />
              </div>

              <div class="form-group">
                <label for="alipayAccount">支付宝号码 *</label>
                <input
                  type="text"
                  id="alipayAccount"
                  v-model="personalInfo.alipayAccount"
                  placeholder="请输入您的支付宝账号"
                  required
                />
              </div>

              <div class="form-group">
                <label for="name">姓名 *</label>
                <input
                  type="text"
                  id="name"
                  v-model="personalInfo.name"
                  placeholder="请输入您的真实姓名"
                  required
                />
              </div>

              <button type="submit" class="save-button">
                保存信息
              </button>
            </form>
          </div>

          <!-- 推广链接 -->
          <div class="promotion-link-section">
            <h3>我的推广链接</h3>
            <div class="link-box">
              <input type="text" readonly :value="promotionLink" />
              <button @click="copyLink(promotionLink)">复制</button>
            </div>
          </div>
        </div>
      </div>

      <!-- 推广数据 -->
      <div v-else-if="activeTab === 'promotion'" class="promotion-section">
        <div class="loading" v-if="loadingTodayStats">
          <p>加载中...</p>
        </div>
        <div v-else>
          <div class="today-data">
            <h3>今日数据</h3>
            <div class="data-grid">
              <div class="data-item">
                <div class="value">{{ todayStats.totalUsers || 0 }}</div>
                <div class="label">总用户数</div>
              </div>
              <div class="data-item">
                <div class="value">{{ todayStats.newUsers || 0 }}</div>
                <div class="label">新用户数</div>
              </div>
              <div class="data-item">
                <div class="value">{{ todayStats.visitors || 0 }}</div>
                <div class="label">访问人数</div>
              </div>
              <div class="data-item">
                <div class="value">{{ todayStats.senders || 0 }}</div>
                <div class="label">发送人数</div>
              </div>
              <div class="data-item">
                <div class="value">{{ todayStats.messages || 0 }}</div>
                <div class="label">发送条数</div>
              </div>
              <div class="data-item">
                <div class="value">¥{{ (todayStats.smsEarnings || 0).toFixed(2) }}</div>
                <div class="label">今日收益</div>
              </div>
            </div>
          </div>

          <div class="history-data">
            <h3>历史数据</h3>
            <div class="history-table-container">
              <table class="history-table">
                <thead>
                  <tr>
                    <th>日期</th>
                    <th>新增用户</th>
                    <th>总用户数</th>
                    <th>发送条数</th>
                    <th>收益(¥)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(record, index) in historyData" :key="index">
                    <td>{{ record.date }}</td>
                    <td>{{ record.newUsers }}</td>
                    <td>{{ record.totalUsers }}</td>
                    <td>{{ record.messages }}</td>
                    <td>{{ (record.earnings || 0).toFixed(2) }}</td>
                  </tr>
                  <tr v-if="historyData.length === 0">
                    <td colspan="5" class="no-data">暂无历史数据</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- 提现数据 -->
      <div v-else-if="activeTab === 'withdrawal'" class="withdrawal-section">
        <div class="loading" v-if="loadingWithdrawals">
          <p>加载中...</p>
        </div>
        <div v-else>
          <div class="withdrawal-info">
            <div class="available-balance">
              <div class="label">可提现余额</div>
              <div class="value">¥{{ (agentStats.availableWithdrawal || 0).toFixed(2) }}</div>
              <div class="description">随时可提现，预计1-3个工作日到账</div>
            </div>

            <div class="withdrawal-form">
              <div class="form-item">
                <label>提现金额</label>
                <input
                  type="number"
                  v-model.number="withdrawAmount"
                  placeholder="请输入提现金额"
                  step="0.01"
                  min="0"
                  :max="agentStats.availableWithdrawal"
                />
                <span class="currency">¥</span>
              </div>

              <div class="quick-select">
                <button class="quick-btn" @click="withdrawAmount = 10">¥10</button>
                <button class="quick-btn" @click="withdrawAmount = 50">¥50</button>
                <button class="quick-btn" @click="withdrawAmount = 100">¥100</button>
                <button class="quick-btn" @click="withdrawAmount = agentStats.availableWithdrawal">全部</button>
              </div>

              <button class="confirm-btn" @click="confirmWithdrawal">
                确认提现
              </button>
            </div>
          </div>

          <div class="withdrawal-history">
            <h3>提现记录</h3>
            <div class="record-list">
              <div v-for="(record, index) in withdrawals" :key="index" class="record-item">
                <div class="date">{{ formatDate(record.createdAt) }}</div>
                <div class="amount" :class="{ positive: record.amount > 0 }">
                  ¥{{ parseFloat(record.amount).toFixed(2) }}
                </div>
                <div class="status" :class="getStatusClass(record.status)">
                  {{ getStatusText(record.status) }}
                </div>
              </div>
              <div v-if="withdrawals.length === 0" class="no-records">
                暂无提现记录
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  apiGetAgent,
  apiGetAgentStats,
  apiGetAgentTodayStats,
  apiGetAgentHistoryData,
  apiGetAgentWithdrawals,
  apiAgentWithdraw,
  apiSaveAgentPersonalInfo
} from '@/utils/api';

export default {
  name: 'AgentCenter',
  data() {
    return {
      activeTab: 'info',
      agent: {},
      agentStats: {
        promotedUsers: 0,
        totalEarnings: 0,
        availableWithdrawal: 0,
        registerDate: '',
        performanceCount: 0
      },
      todayStats: {
        totalUsers: 0,
        newUsers: 0,
        visitors: 0,
        senders: 0,
        messages: 0,
        smsEarnings: 0,
        agentEarnings: 0
      },
      withdrawals: [],
      historyData: [],
      withdrawAmount: 0,
      loadingStats: false,
      loadingTodayStats: false,
      loadingWithdrawals: false,
      commissionRate: 40,
      personalInfo: {
        wechatId: '',
        wechatPublic: '',
        alipayAccount: '',
        name: ''
      }
    };
  },
  computed: {
    promotionLink() {
      if (!this.agent.id) return '';
      return `${window.location.origin}/?agent=${this.agent.id}`;
    }
  },
  async mounted() {
    await this.loadAgentData();
    await this.loadAgentStats();
    await this.loadTodayStats();
    await this.loadWithdrawals();
    await this.loadHistoryData();
  },
  methods: {
    getCurrentAgentId() {
      const agentId = localStorage.getItem('agentId');
      if (agentId) {
        return agentId;
      } else {
        console.error('未找到代理ID');
        return null;
      }
    },

    async savePersonalInfo() {
      try {
        if (!this.personalInfo.alipayAccount || !this.personalInfo.name) {
          alert('支付宝账号和姓名为必填项');
          return;
        }

        const agentId = this.getCurrentAgentId();
        if (!agentId) {
          alert('未找到代理信息');
          return;
        }

        const response = await apiSaveAgentPersonalInfo(agentId, this.personalInfo);
        if (response.success) {
          alert('个人信息保存成功');
        } else {
          alert('保存失败: ' + response.error);
        }
      } catch (error) {
        console.error('保存个人信息失败:', error);
        alert('保存失败，请稍后重试');
      }
    },

    async loadAgentData() {
      try {
        const agentId = this.getCurrentAgentId();
        if (!agentId) {
          console.error('未找到代理ID');
          return;
        }

        const response = await apiGetAgent(agentId);
        if (response.success) {
          this.agent = response.agent;

          // 根据link字段设置佣金比例
          if (this.agent.link === 'high') {
            this.commissionRate = 40;
          } else if (this.agent.link === 'middle') {
            this.commissionRate = 30;
          } else {
            this.commissionRate = 40;
          }

          // 填充个人信息
          this.personalInfo.wechatId = this.agent.wechatId || '';
          this.personalInfo.wechatPublic = this.agent.wechatPublic || '';
          this.personalInfo.alipayAccount = this.agent.alipayAccount || '';
          this.personalInfo.name = this.agent.name || '';
        }
      } catch (error) {
        console.error('获取代理信息失败:', error);
      }
    },

    async loadAgentStats() {
      try {
        this.loadingStats = true;
        const agentId = this.getCurrentAgentId();
        if (!agentId) return;

        const response = await apiGetAgentStats(agentId);
        if (response.success) {
          this.agentStats = response.stats;
        }
      } catch (error) {
        console.error('获取代理统计数据失败:', error);
      } finally {
        this.loadingStats = false;
      }
    },

    async loadTodayStats() {
      try {
        this.loadingTodayStats = true;
        const agentId = this.getCurrentAgentId();
        if (!agentId) return;

        const response = await apiGetAgentTodayStats(agentId);
        if (response.success) {
          this.todayStats = response.stats;
        }
      } catch (error) {
        console.error('获取今日统计数据失败:', error);
      } finally {
        this.loadingTodayStats = false;
      }
    },

    async loadWithdrawals() {
      try {
        this.loadingWithdrawals = true;
        const agentId = this.getCurrentAgentId();
        if (!agentId) return;

        const response = await apiGetAgentWithdrawals(agentId);
        if (response.success) {
          this.withdrawals = response.withdrawals;
        }
      } catch (error) {
        console.error('获取提现记录失败:', error);
      } finally {
        this.loadingWithdrawals = false;
      }
    },

    async loadHistoryData() {
      try {
        const agentId = this.getCurrentAgentId();
        if (!agentId) return;

        const response = await apiGetAgentHistoryData(agentId);
        if (response.success) {
          this.historyData = response.data || [];
        }
      } catch (error) {
        console.error('获取历史数据失败:', error);
      }
    },

    async confirmWithdrawal() {
      if (this.withdrawAmount <= 0) {
        alert('请输入有效的提现金额');
        return;
      }

      if (this.withdrawAmount > this.agentStats.availableWithdrawal) {
        alert('提现金额不能超过可用余额');
        return;
      }

      try {
        const agentId = this.getCurrentAgentId();
        if (!agentId) {
          alert('未找到代理信息');
          return;
        }

        const response = await apiAgentWithdraw(agentId, this.withdrawAmount);
        if (response.success) {
          alert('提现申请已提交');
          this.withdrawAmount = 0;
          await this.loadAgentStats();
          await this.loadWithdrawals();
        } else {
          alert('提现申请失败: ' + response.error);
        }
      } catch (error) {
        console.error('提现申请失败:', error);
        alert('提现申请失败，请稍后重试');
      }
    },

    copyLink(link) {
      navigator.clipboard.writeText(link).then(() => {
        alert('链接已复制到剪贴板');
      }).catch(err => {
        console.error('复制失败:', err);
        alert('复制失败');
      });
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString();
    },

    getAgentLevel(link) {
      if (link === 'high') return '高级代理';
      if (link === 'middle') return '中级代理';
      return '初级代理';
    },

    getStatusText(status) {
      const statusMap = {
        'pending': '待处理',
        'processing': '处理中',
        'completed': '已完成',
        'rejected': '已拒绝'
      };
      return statusMap[status] || '未知状态';
    },

    getStatusClass(status) {
      return `status-${status}`;
    }
  }
};
</script>

<style scoped>
.agent-center {
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  background-color: #faf8f5;
  color: #333;
  min-height: 100vh;
  padding-bottom: 20px;
}

.page-header {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: white;
  border-bottom: 1px solid #eee;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: none;
  background: #f5f5f5;
  border-radius: 20px;
  color: #666;
  font-size: 14px;
  cursor: pointer;
}

.back-button svg {
  width: 16px;
  height: 16px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.nav-tabs {
  display: flex;
  background-color: white;
  border-bottom: 1px solid #eee;
}

.tab-button {
  flex: 1;
  padding: 16px 0;
  text-align: center;
  font-size: 14px;
  color: #666;
  border: none;
  background: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-button.active {
  color: #ff9500;
  border-bottom: 2px solid #ff9500;
}

.content-area {
  padding: 16px;
}

.loading {
  text-align: center;
  padding: 40px 0;
  color: #666;
}

/* 我的信息样式 */
.stats-bar {
  display: flex;
  justify-content: space-around;
  background: linear-gradient(135deg, #ff9500 0%, #ffb347 100%);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  color: white;
}

.stat-item {
  text-align: center;
}

.stat-item .value {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 4px;
}

.label-bar {
  font-size: 12px;
  opacity: 0.9;
}

.account-overview {
  background: white;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
}

.item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.item:last-child {
  border-bottom: none;
}

.item .label {
  font-size: 14px;
  color: #666;
}

.item .value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

/* 个人信息模块样式 */
.personal-info-section,
.promotion-link-section {
  background: white;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
}

.personal-info-section h3,
.promotion-link-section h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #333;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.form-group input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #ff9500;
}

.save-button {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #ff9500 0%, #ffb347 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
}

/* 推广链接样式 */
.link-box {
  display: flex;
  gap: 8px;
}

.link-box input {
  flex: 1;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: #f5f5f5;
}

.link-box button {
  padding: 12px 20px;
  background: #ff9500;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
}

/* 推广数据样式 */
.today-data,
.history-data {
  background: white;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
}

.today-data h3,
.history-data h3,
.withdrawal-history h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #333;
}

.data-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.data-item {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}

.data-item .value {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
}

.data-item .label {
  font-size: 12px;
  color: #666;
}

/* 历史数据表格 */
.history-table-container {
  overflow-x: auto;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.history-table th,
.history-table td {
  padding: 12px 8px;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
}

.history-table th {
  background: #f8f9fa;
  font-weight: 500;
  color: #666;
}

.no-data {
  text-align: center;
  color: #999;
  padding: 20px;
}

/* 提现样式 */
.withdrawal-info {
  background: white;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
}

.available-balance {
  text-align: center;
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
}

.available-balance .label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.available-balance .value {
  font-size: 32px;
  font-weight: bold;
  color: #ff9500;
}

.available-balance .description {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
}

.withdrawal-form .form-item {
  position: relative;
  margin-bottom: 16px;
}

.withdrawal-form label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.withdrawal-form input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  box-sizing: border-box;
}

.currency {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #666;
}

.quick-select {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.quick-btn {
  flex: 1;
  padding: 10px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
}

.confirm-btn {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
}

/* 提现记录 */
.withdrawal-history {
  background: white;
  border-radius: 16px;
  padding: 16px;
}

.record-list {
  max-height: 400px;
  overflow-y: auto;
}

.record-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.record-item:last-child {
  border-bottom: none;
}

.date {
  font-size: 13px;
  color: #666;
}

.amount {
  font-size: 15px;
  font-weight: 500;
  color: #333;
}

.amount.positive {
  color: #4caf50;
}

.status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
}

.status-processing {
  background: #cce5ff;
  color: #004085;
}

.status-completed {
  background: #d4edda;
  color: #155724;
}

.status-rejected {
  background: #f8d7da;
  color: #721c24;
}

.no-records {
  text-align: center;
  color: #999;
  padding: 20px;
}
</style>
